/**
 * Client copies of the server's shared field rules (`Willnq.SharedKernel.Validation`), so a form says
 * what the API will enforce before the round trip (FrontendGuidelines §8.1). The server stays the
 * authority; each function here names its C# counterpart, and the two change together.
 *
 * None of these restrict the alphabet: names, places and titles are written in every script, with
 * accents, apostrophes and hyphens, and titles carry technical symbols such as C++, C# and .NET.
 */

/** `WebUrl.MaxLength` — every URL column on the platform. */
export const WEB_URL_MAX_LENGTH = 2048;

export const WEB_URL_MESSAGE = 'Enter a full web address starting with http:// or https://';

/**
 * `WebUrl.IsValid`: an absolute http(s) URL with a dotted host, any path/query/fragment/port, and no
 * embedded credentials, whitespace or other scheme (`javascript:`, `data:`, `ftp:`…).
 */
export function isWebUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed.length > WEB_URL_MAX_LENGTH || /[\s\p{Cc}]/u.test(trimmed)) return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (url.username !== '' || url.password !== '') return false;

  const host = url.hostname;
  return host.includes('.') && !host.startsWith('.') && !host.endsWith('.');
}

/** A bare domain/path (e.g. `acme.com/jobs`) is assumed https:// rather than rejected. */
export const withHttps = (value: string): string =>
  value === '' || /^https?:\/\//i.test(value) ? value : `https://${value}`;

/** `TextRules.IsMeaningfulText`: at least one letter or digit, no control characters. */
export const isMeaningfulText = (value: string): boolean =>
  /[\p{L}\p{N}]/u.test(value) && !/\p{Cc}/u.test(value);

/**
 * `TextRules.IsPersonName`: at least one letter; otherwise letters, combining marks, spaces,
 * apostrophes, hyphens and periods — "Anne-Marie", "O'Brien", "D’Souza", "J. R.", "José", "李".
 */
export const isPersonName = (value: string): boolean => {
  const trimmed = value.trim();
  return /\p{L}/u.test(trimmed) && /^[\p{L}\p{M} '’‘\-‐.]+$/u.test(trimmed);
};

/**
 * `TextRules.IsPlaceName`: at least one letter; otherwise letters, marks, digits, spaces and
 * `. , ' ’ - ( ) & /` — "São Paulo", "Stratford-upon-Avon", "St. John's", "Washington, D.C.".
 */
export const isPlaceName = (value: string): boolean => {
  const trimmed = value.trim();
  return /\p{L}/u.test(trimmed) && /^[\p{L}\p{M}\p{N} '’‘\-‐.,()&/]+$/u.test(trimmed);
};

/** `TextRules.MinReasonSignificantCharacters`. */
export const MIN_REASON_SIGNIFICANT_CHARACTERS = 3;

export const REASON_MESSAGE = 'Give a short explanation (at least a few words or letters)';

/** `TextRules.IsMeaningfulReason`: at least three letters or digits ("Spam" yes, "." and "ok" no). */
export const isMeaningfulReason = (value: string): boolean =>
  (value.match(/[\p{L}\p{N}]/gu)?.length ?? 0) >= MIN_REASON_SIGNIFICANT_CHARACTERS;

/** `PhoneNumberRule.MaxLength`. */
export const CONTACT_PHONE_MAX_LENGTH = 30;

export const CONTACT_PHONE_MESSAGE = 'Enter a phone number of 7–15 digits, e.g. +91 80 1234 5678';

/** `PhoneNumberRule.IsValid`: optional leading +, digits with space/-/./() grouping, 7–15 digits. */
export const isContactPhone = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed.length > CONTACT_PHONE_MAX_LENGTH || !/^\+?[0-9 ().-]+$/.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
};

export const POSTAL_CODE_MESSAGE = 'Enter a valid postal code';
export const INDIA_PIN_MESSAGE = 'Enter a 6-digit PIN code, e.g. 560001';

/**
 * `PostalCodeRule.IsValid`: India's six-digit PIN when the country is India; otherwise 2–10 letters,
 * digits, spaces or hyphens containing at least one digit ("SW1A 1AA", "10115", "12345-6789").
 */
export const isPostalCode = (value: string, isIndia: boolean): boolean => {
  const trimmed = value.trim();
  if (isIndia) return /^[1-9][0-9]{5}$/.test(trimmed);
  return /^[A-Za-z0-9][A-Za-z0-9 -]{0,8}[A-Za-z0-9]$/.test(trimmed) && /[0-9]/.test(trimmed);
};

export const EMAIL_MESSAGE = 'Enter a valid email address, like name@example.com';

/** `EmailAddressRule.IsValid`: one @, no whitespace, a dot in the domain, at most 320 characters. */
export const isEmailAddress = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.length <= 320 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed);
};

/** `FieldRuleExtensions.MaxPasswordUtf8Bytes` — the most BCrypt can tell apart. */
export const PASSWORD_MAX_UTF8_BYTES = 72;

export const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).length;

/** `FieldRuleExtensions.EarliestHistoricalDate`. */
export const EARLIEST_HISTORICAL_DATE = new Date(1900, 0, 1);

/**
 * `FieldRuleExtensions.HistoricalDate`: between 1900 and today. Compared by calendar day in the
 * user's own time zone, which is the date the picker shows them.
 */
export const isHistoricalDate = (value: Date, now: Date = new Date()): boolean => {
  const day = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return day >= EARLIEST_HISTORICAL_DATE && day <= today;
};
